import { Store } from 'rxjs-observable-store';
import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, forkJoin, Observable, of, Subscription } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, distinctUntilChanged, filter, finalize, map, shareReplay, switchMap, timeout } from 'rxjs/operators';

import { ToursCatalog } from 'package-types';

import { HotelPageInfoState } from './hotel-page-info.state';
import { ToursCatalogEndpoint } from '@services/api/tours-catalog.endpoint';
import { InformationStore } from '@services/store/information.store';
import { ImagesEndpoint } from '@services/api/images.endpoint';
import { PlatformService } from '@services/platform.service';
import { NotificationService } from '@services/notification.service';
import { ErrorMessage } from '@constants/error-message.enum';
import { collectImagesIds } from '@helpers/images.helper';
import { buildFacilities, buildRating, enrichReviewsByImageLinks } from '../helpers/hotel-page-info.helper';
import { AppConfig, APP_CONFIG } from '../../../../app-config.module';
import { onLangChange } from '@helpers/language.helper';

import { ReviewsSource } from '@interfaces/hotel-review';
import { Hotel, HotelPageInfo } from '@interfaces/services/api/tours-catalog.endpoint';

@UntilDestroy()
@Injectable()
export class HotelPageInfoStore extends Store<HotelPageInfoState> {
  private readonly hotel$ = this.loadAndShareHotelPageInfo();
  private reviewsSubscriptions: Partial<Record<ReviewsSource, Subscription>> = { };
  private reviewsHotelId?: number;
  private reviewOffsets: Record<ReviewsSource, number> = { site: 0, turpravda: 0 };
  private failedReviewOffsets: Partial<Record<ReviewsSource, number>> = { };

  constructor(
    private translate: TranslateService,
    private toursCatalog: ToursCatalogEndpoint,
    private images: ImagesEndpoint,
    private informationStore: InformationStore,
    private platform: PlatformService,
    private notification: NotificationService,
    @Inject(APP_CONFIG) private config: AppConfig
  ) {
    super(new HotelPageInfoState());
  }

  init(): void {
    this.setHotelInfoOnHotelChange();
    this.loadImagesOnHotelChange();
    this.loadReviewsOnHotelChange();
  }

  loadNextPage(source: ReviewsSource = 'turpravda'): void {
    const loading = source === 'site' ? this.state.ownReviewsLoading : this.state.reviewsLoading;
    const last = source === 'site' ? this.state.ownLastReviewsPage : this.state.isLastReviewsPage;
    if (this.reviewsHotelId !== undefined && !loading && !last) {
      this.loadReviewsPage(this.reviewsHotelId, source);
    }
  }

  retryReviews(source: ReviewsSource = 'turpravda'): void {
    const loading = source === 'site' ? this.state.ownReviewsLoading : this.state.reviewsLoading;
    if (this.reviewsHotelId !== undefined && !loading) {
      this.loadReviewsPage(this.reviewsHotelId, source, true);
    }
  }

  private loadAndShareHotelPageInfo(): Observable<Hotel> {
    return this.informationStore.onChanges('pageInfo').pipe(
      filter((pageInfo): pageInfo is HotelPageInfo => pageInfo?.type === ToursCatalog.PageType.Hotel),
      map(({ hotel }) => hotel),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private setHotelInfoOnHotelChange() {
    combineLatest([this.hotel$, onLangChange(this.translate)])
      .pipe(
        distinctUntilChanged((prev, curr) => {
          const [prevHotel, prevLang] = prev;
          const [currHotel, currLang] = curr;

          return prevHotel.hotelId === currHotel.hotelId && prevLang === currLang;
        }),
        untilDestroyed(this)
      )
      .subscribe(([hotel, lang]) => this.setState({
        ...this.state,
        hotelInfo: hotel,
        facilities: buildFacilities(hotel, lang),
        rating: buildRating(hotel),
        commonDescription: hotel.descriptions[lang]?.common
      }));
  }

  private loadImagesOnHotelChange() {
    this.hotel$.pipe(
      switchMap(({ hotelId }) => forkJoin({
        hotelId: of(hotelId),
        images: this.images.getHotelImagesLinks([hotelId])
      })),
      map(({ hotelId, images: { imageResizesLinks } }) => imageResizesLinks[hotelId] ? Object.values(imageResizesLinks[hotelId]) : []),
      untilDestroyed(this)
    ).subscribe({
      next: images => this.patchState(images, 'images'),
      error: () => this.notification.error(ErrorMessage.ImageLoadingFailure)
    });
  }

  private loadReviewsOnHotelChange() {
    this.hotel$.pipe(
      this.platform.accessForBrowser(),
      map(({ hotelId }) => hotelId),
      distinctUntilChanged(),
      untilDestroyed(this)
    ).subscribe(hotelId => {
      Object.values(this.reviewsSubscriptions).forEach(subscription => subscription?.unsubscribe());
      this.reviewsHotelId = hotelId;
      this.reviewOffsets = { site: 0, turpravda: 0 };
      this.failedReviewOffsets = { };
      this.setState({
        ...this.state, reviews: [], reviewsSource: 'turpravda', reviewsPaginationLimited: false,
        reviewsError: false, reviewsLoading: false, isLastReviewsPage: false,
        ownReviews: [], ownReviewsError: false, ownReviewsLoading: false, ownLastReviewsPage: false
      });
      this.loadReviewsPage(hotelId, 'turpravda');
      this.loadReviewsPage(hotelId, 'site');
    });
  }

  private loadReviewsPage(hotelId: number, source: ReviewsSource, retry = false): void {
    const own = source === 'site';
    const requestedSkip = retry ? (this.failedReviewOffsets[source] ?? 0) : this.reviewOffsets[source];
    if (own) {
      this.setState({ ...this.state, ownReviewsLoading: true, ownReviewsError: false });
    } else {
      this.setState({ ...this.state, reviewsLoading: true, reviewsError: false });
    }
    this.reviewsSubscriptions[source] = this.toursCatalog.getHotelReviews(
      hotelId, requestedSkip, this.config.pages.hotelPageInfo.reviewsPerPage, undefined, source
    ).pipe(
      switchMap(response => {
        const imagesIds = collectImagesIds(response.reviews);
        const links$ = imagesIds.length
          ? this.images.getDestinationReviewImages(imagesIds).pipe(
            timeout(10000),
            map(({ imageResizesLinks }) => imageResizesLinks),
            catchError(() => of({ }))
          )
          : of({ });
        return links$.pipe(map(links => ({ ...response, reviews: enrichReviewsByImageLinks(response.reviews, links) })));
      }),
      finalize(() => {
        if (hotelId === this.reviewsHotelId) {
          this.patchState(false, own ? 'ownReviewsLoading' : 'reviewsLoading');
        }
      }),
      untilDestroyed(this)
    ).subscribe({
      next: ({ reviews, hasNext, paginationLimited, partnerError }) => {
        if (hotelId !== this.reviewsHotelId) {
          return;
        }
        if (partnerError) {
          this.failedReviewOffsets[source] = requestedSkip;
          if (!reviews.length) {
            this.patchState(true, own ? 'ownReviewsError' : 'reviewsError');
            return;
          }
        } else {
          delete this.failedReviewOffsets[source];
        }
        const previous = (own ? this.state.ownReviews : this.state.reviews).slice(0, requestedSkip);
        this.reviewOffsets[source] = requestedSkip + reviews.length;
        if (own) {
          this.setState({
            ...this.state, ownReviews: [...previous, ...reviews],
            ownLastReviewsPage: !hasNext || reviews.length === 0, ownReviewsError: !!partnerError
          });
        } else {
          this.setState({
            ...this.state, reviews: [...previous, ...reviews], reviewsSource: 'turpravda',
            isLastReviewsPage: !!paginationLimited || !hasNext || reviews.length === 0,
            reviewsPaginationLimited: !!paginationLimited, reviewsError: !!partnerError
          });
        }
      },
      error: () => {
        if (hotelId === this.reviewsHotelId) {
          this.failedReviewOffsets[source] = requestedSkip;
          this.patchState(true, own ? 'ownReviewsError' : 'reviewsError');
        }
      }
    });
  }
}
